import { useState, useEffect, useCallback } from "react";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/ui/tabs";
import {
  SearchIcon,
  DownloadIcon,
  UploadIcon,
  PlusIcon,
  Loader2,
} from "lucide-react";
import { Badge } from "@shared/ui/badge";
import DashboardTab from "../dashboard/DashboardTab";
import ServiceAreaTable from "../list/EntityTable";
import AddEntityModal from "../add/AddEntityModal";
import ImportModal from "../import/ImportModal";
import ExportModal from "../export/ExportModal";
import EntityDetailView from "../view/EntityDetailView";
import { useTerritoryCount } from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { onboardingApi } from "../../../api";

const ServiceAreaLanding = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [modalEntityType, setModalEntityType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedSearchEntity, setSelectedSearchEntity] = useState<any>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);

  // Search related states
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  const { data: territoryData, isLoading: isLoadingCounts } =
    useTerritoryCount();

  const tabData = [
    { id: "dashboard", label: "Dashboard", count: null, configLevel: "" },
    {
      id: "region",
      label: "Region",
      count: territoryData?.result.regionStats,
      configLevel: "region",
    },
    {
      id: "country",
      label: "Country",
      count: territoryData?.result.countryStats,
      configLevel: "country",
    },
    {
      id: "state",
      label: "State",
      count: territoryData?.result.stateStats,
      configLevel: "state",
    },
    {
      id: "city",
      label: "City/County",
      count: territoryData?.result.countyStats,
      configLevel: "county",
    },
    {
      id: "zone",
      label: "Zone",
      count: territoryData?.result.zoneStats,
      configLevel: "zone",
    },
    {
      id: "division",
      label: "Division",
      count: territoryData?.result.divisionStats,
      configLevel: "division",
    },
    {
      id: "areas",
      label: "Areas",
      count: territoryData?.result.areaStats,
      configLevel: "area",
    },
    {
      id: "sub-areas",
      label: "Sub-Areas",
      count: territoryData?.result.subAreaStats,
      configLevel: "sub_area",
    },
    {
      id: "premises",
      label: "Premises",
      count: territoryData?.result.premiseStats,
      configLevel: "premise",
    },
  ];

  // Debounced search function
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearchLoading(true);
      setSearchError("");

      try {
        const response = await onboardingApi.searchEntities(
          remoteUtilityId,
          searchQuery.trim()
        );

        // Transform API results to enhanced search results
        const enhancedResults = response.result.map((item: any) => {
          // Map territory_type to display name and tab ID
          let displayName = "";
          let tabId = "";

          switch (item.territoryType) {
            case "region":
              displayName = "Region";
              tabId = "region";
              break;
            case "country":
              displayName = "Country";
              tabId = "country";
              break;
            case "state":
              displayName = "State";
              tabId = "state";
              break;
            case "county":
              displayName = "City/County";
              tabId = "city";
              break;
            case "zone":
              displayName = "Zone";
              tabId = "zone";
              break;
            case "division":
              displayName = "Division";
              tabId = "division";
              break;
            case "area":
              displayName = "Areas";
              tabId = "areas";
              break;
            case "sub_area":
              displayName = "Sub-Areas";
              tabId = "sub-areas";
              break;
            case "premise":
              displayName = "Premises";
              tabId = "premises";
              break;
            default:
              displayName = item.territoryType;
              tabId = "";
          }

          return {
            ...item,
            id: item.code,
            type: displayName,
            entity: tabId,
          };
        });

        setSearchResults(enhancedResults);
        setShowSearchResults(enhancedResults.length > 0);
      } catch (error) {
        console.error("Search failed:", error);
        setSearchError("Search failed. Please try again.");
        setSearchResults([]);
        setShowSearchResults(false);
      } finally {
        setIsSearchLoading(false);
      }
    },
    [remoteUtilityId]
  );

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm, performSearch]);

  const handleAddEntity = (entityType: string = "") => {
    setModalEntityType(entityType);
    setIsAddModalOpen(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.length === 0) {
      setShowSearchResults(false);
      setSearchResults([]);
      setSearchError("");
    }
  };

  const selectSearchResult = (result: any) => {
    console.log("selcted item is here man", result);
    setSearchTerm(result.name);
    setShowSearchResults(false);
    setSelectedSearchEntity(result);
    setIsDetailViewOpen(true);
  };

  const handleBackFromDetail = () => {
    setIsDetailViewOpen(false);
    setSelectedSearchEntity(null);
    setSearchTerm("");
  };

  // Close search results when clicking outside
  const handleSearchBlur = () => {
    // Add a small delay to allow click events to register
    setTimeout(() => {
      setShowSearchResults(false);
    }, 150);
  };

  const handleSearchFocus = () => {
    if (searchResults.length > 0 && searchTerm.length >= 2) {
      setShowSearchResults(true);
    }
  };

  if (isDetailViewOpen && selectedSearchEntity) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto p-6">
          <EntityDetailView
            result={selectedSearchEntity}
            onBack={handleBackFromDetail}
            // onEdit={() => {
            //   console.log("Edit entity:", selectedSearchEntity);
            // }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      <div className="flex flex-col gap-2">
        {/* Page Header */}
        <div className="mb-6">
          <p className="page-title">
            Service Areas Management
          </p>
          <p className="page-subtitle">
            Manage utility service territories and premises across all regions
          </p>
        </div>

        {/* Action Bar */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex gap-3 flex-1 lg:flex-initial lg:min-w-96">
              <div className="relative flex-1">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                {isSearchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
                )}
                <Input
                  placeholder="Search across all entities... (min 2 characters)"
                  className="pl-10 pr-10"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onBlur={handleSearchBlur}
                  onFocus={handleSearchFocus}
                />

                {/* Search Results Dropdown */}
                {showSearchResults && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-t-0 rounded-b-md shadow-lg z-50 max-h-64 overflow-y-auto">
                    {searchError ? (
                      <div className="p-3 text-red-500 text-sm">
                        {searchError}
                      </div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((result: any) => (
                        <div
                          key={result.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between border-b last:border-b-0"
                          onMouseDown={() => selectSearchResult(result)} // Use onMouseDown to prevent onBlur from firing first
                        >
                          <div className="flex-1">
                            <span className="text-gray-900 font-medium">
                              {result.name}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              Created by {result.createdBy} •{" "}
                              {result.created_date}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              {result.type}
                            </Badge>
                            {!result.isActive && (
                              <Badge variant="destructive" className="text-xs">
                                Inactive
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))
                    ) : searchTerm.length >= 2 && !isSearchLoading ? (
                      <div className="p-3 text-gray-500 text-sm">
                        No results found for "{searchTerm}"
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => handleAddEntity()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Entity
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsImportModalOpen(true)}
              >
                <UploadIcon className="w-4 h-4 mr-2" />
                Import Data
              </Button>
              {/* <Button
                variant="outline"
                onClick={() => setIsExportModalOpen(true)}
              >
                <DownloadIcon className="w-4 h-4 mr-2" />
                Export Data
              </Button> */}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <Tabs
          className="w-full mt-4"
          tabsListClassName="bg-white rounded-lg shadow-sm border p-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-10 gap-2 mb-6"
          tabComponents={{
            dashboard: {
              label: "Dashboard",
              component: <DashboardTab />,
              className: "py-3 px-2",
            },
            region: {
              label: `Region (${tabData[1].count})`,
              component: (
                <ServiceAreaTable
                  entityType="Region"
                  configLevel="region"
                  onEditEntity={(entity) => console.log("Edit region:", entity)}
                />
              ),
              className: "py-3 px-2",
            },
            country: {
              label: `Country (${tabData[2].count})`,
              component: (
                <ServiceAreaTable
                  entityType="Country"
                  configLevel="country"
                  onEditEntity={(entity) =>
                    console.log("Edit country:", entity)
                  }
                />
              ),
              className: "py-3 px-2",
            },
            state: {
              label: `State (${tabData[3].count})`,
              component: (
                <ServiceAreaTable
                  entityType="State"
                  configLevel="state"
                  onEditEntity={(entity) => console.log("Edit state:", entity)}
                />
              ),
              className: "py-3 px-2",
            },
            city: {
              label: `City/County (${tabData[4].count})`,
              component: (
                <ServiceAreaTable
                  entityType="City/County"
                  configLevel="county"
                  onEditEntity={(entity) => console.log("Edit county:", entity)}
                />
              ),
              className: "py-3 px-2",
            },
            zone: {
              label: `Zone (${tabData[5].count})`,
              component: (
                <ServiceAreaTable
                  entityType="Zone"
                  configLevel="zone"
                  onEditEntity={(entity) => console.log("Edit zone:", entity)}
                />
              ),
              className: "py-3 px-2",
            },
            division: {
              label: `Division (${tabData[6].count})`,
              component: (
                <ServiceAreaTable
                  entityType="Division"
                  configLevel="division"
                  onEditEntity={(entity) =>
                    console.log("Edit division:", entity)
                  }
                />
              ),
              className: "py-3 px-2",
            },
            areas: {
              label: `Areas (${tabData[7].count})`,
              component: (
                <ServiceAreaTable
                  entityType="Areas"
                  configLevel="area"
                  onEditEntity={(entity) => console.log("Edit area:", entity)}
                />
              ),
              className: "py-3 px-2",
            },
            "sub-areas": {
              label: `Sub-Areas (${tabData[8].count})`,
              component: (
                <ServiceAreaTable
                  entityType="Sub-Areas"
                  configLevel="sub_area"
                  onEditEntity={(entity) =>
                    console.log("Edit sub-area:", entity)
                  }
                />
              ),
              className: "py-3 px-2",
            },
            premises: {
              label: `Premises (${tabData[9].count})`,
              component: (
                <ServiceAreaTable
                  entityType="Premises"
                  configLevel="premise"
                  onEditEntity={(entity) =>
                    console.log("Edit premise:", entity)
                  }
                />
              ),
              className: "py-3 px-2",
            },
          }}
        />

        {/* Modals */}
        <AddEntityModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          entityType={modalEntityType}
        />
        <ImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
        />
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default ServiceAreaLanding;
